"use client";

import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  Input,
  type TanstackReactTable,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDownIcon, ArrowUpIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTime } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { WalletTransactionItem } from "../api/client";
import { useWalletTransactionsQuery } from "../api/queries";

const TYPE_OPTIONS = [
  { value: "all", label: "ປະເພດທັງໝົດ" },
  { value: "topup", label: "ເຕີມເງິນ" },
  { value: "pay_order", label: "ຈ່າຍຄຳສັ່ງຊື້" },
  { value: "pay_job", label: "ຈ່າຍຄ່າງານ" },
  { value: "refund", label: "ຄືນເງິນ" },
  { value: "payout", label: "ຖອນເງິນ" },
  { value: "fee", label: "ຄ່າທຳນຽມ" },
  { value: "adjust", label: "ປັບປຸງ" },
] as const;

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "pending", label: "ລໍຖ້າ" },
  { value: "posted", label: "ສຳເລັດ" },
  { value: "failed", label: "ລົ້ມເຫຼວ" },
  { value: "reversed", label: "ກັບຄືນ" },
] as const;

const REFERENCE_TYPE_OPTIONS = [
  { value: "all", label: "ອ້າງອີງທັງໝົດ" },
  { value: "order", label: "ຄຳສັ່ງຊື້" },
  { value: "job", label: "ງານ" },
  { value: "pos_sale", label: "POS" },
  { value: "manual", label: "ແມນນວນ" },
] as const;

const TYPE_LABELS: Record<string, string> = {
  topup: "ເຕີມເງິນ",
  pay_order: "ຈ່າຍຄຳສັ່ງຊື້",
  pay_job: "ຈ່າຍຄ່າງານ",
  refund: "ຄືນເງິນ",
  payout: "ຖອນເງິນ",
  fee: "ຄ່າທຳນຽມ",
  adjust: "ປັບປຸງ",
};

const TYPE_VARIANT: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  topup: "default",
  pay_order: "secondary",
  pay_job: "secondary",
  refund: "outline",
  payout: "destructive",
  fee: "destructive",
  adjust: "outline",
};

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  pending: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າ",
  },
  posted: {
    dot: "bg-success",
    text: "text-success",
    label: "ສຳເລັດ",
  },
  failed: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ລົ້ມເຫຼວ",
  },
  reversed: {
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    label: "ກັບຄືນ",
  },
};

const CREDIT_TYPES = new Set(["topup", "refund"]);

export function WalletTransactionsPage() {
  const nav = useNavigate({ from: "/app/wallet/transactions" });
  const search: OffsetPageQueryDTO & { walletId?: string; search?: string } =
    useSearch({ from: "/app/wallet/transactions" });

  const list = useWalletTransactionsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
    walletId: search.walletId,
    search: search.search,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const typeFilter = findCondition(filters, "type");
  const statusFilter = findCondition(filters, "status");
  const refTypeFilter = findCondition(filters, "referenceType");

  const [searchValue, setSearchValue] = useState<string>(search.search || "");

  useEffect(() => {
    setSearchValue(search.search || "");
  }, [search.search]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        offset: 0,
        search: val || undefined,
      },
    });
  }, 400);

  const typeValue = (typeFilter?.value as string) || "all";
  const statusValue = (statusFilter?.value as string) || "all";
  const refTypeValue = (refTypeFilter?.value as string) || "all";
  const hasFilter =
    !!search.search ||
    typeValue !== "all" ||
    statusValue !== "all" ||
    refTypeValue !== "all";

  const columns: TanstackReactTable.ColumnDef<WalletTransactionItem>[] = [
    createSortableColumn<WalletTransactionItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "type",
      header: "ປະເພດ",
      cell: ({ row }) => {
        const t = row.original.type;
        return (
          <Badge variant={TYPE_VARIANT[t] ?? "outline"} className="text-xs">
            {TYPE_LABELS[t] ?? t}
          </Badge>
        );
      },
      size: 140,
    },
    {
      id: "amount",
      header: "ຈຳນວນເງິນ",
      cell: ({ row }) => {
        const tx = row.original;
        const isCredit = CREDIT_TYPES.has(tx.type);
        return (
          <div className="flex items-center gap-1.5">
            {isCredit ? (
              <ArrowDownIcon className="size-4 text-success" />
            ) : (
              <ArrowUpIcon className="size-4 text-destructive" />
            )}
            <span
              className={`font-semibold tabular-nums ${isCredit ? "text-success" : "text-destructive"}`}
            >
              {isCredit ? "+" : "-"}
              {formatPrice(tx.amount, tx.currency)}
            </span>
          </div>
        );
      },
      size: 180,
    },
    {
      id: "balanceAfter",
      header: "ຍອດຄົງເຫຼືອ",
      cell: ({ row }) => {
        const ba = row.original.balanceAfter;
        if (ba == null) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-sm tabular-nums">
            {formatPrice(ba, row.original.currency)}
          </span>
        );
      },
      size: 160,
    },
    {
      id: "reference",
      header: "ອ້າງອີງ",
      cell: ({ row }) => {
        const tx = row.original;
        if (!tx.referenceType) {
          return <span className="text-muted-foreground">—</span>;
        }
        const refLabel: Record<string, string> = {
          order: "ຄຳສັ່ງຊື້",
          job: "ງານ",
          pos_sale: "POS",
          manual: "ແມນນວນ",
        };
        return (
          <div className="min-w-0">
            <div className="text-sm">
              {refLabel[tx.referenceType] ?? tx.referenceType}
            </div>
            {tx.referenceId && (
              <div className="truncate text-muted-foreground text-xs">
                #{tx.referenceId.slice(0, 8)}
              </div>
            )}
          </div>
        );
      },
      size: 140,
    },
    {
      id: "owner",
      header: "ເຈົ້າຂອງ",
      cell: ({ row }) => {
        const tx = row.original;
        if (!tx.ownerName) {
          return <span className="text-muted-foreground">—</span>;
        }
        const ownerTypeLabel: Record<string, string> = {
          customer: "ລູກຄ້າ",
          shop: "ຮ້ານຄ້າ",
          mechanic: "ຊ່າງ",
          platform: "ແພລດຟອມ",
        };
        return (
          <div className="min-w-0">
            <div className="truncate font-medium text-sm">{tx.ownerName}</div>
            <div className="text-muted-foreground text-xs">
              {ownerTypeLabel[tx.ownerType ?? ""] ?? tx.ownerType}
            </div>
          </div>
        );
      },
      size: 160,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 120,
    },
  ];

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, {
        field,
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ທຸລະກຳ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between space-y-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">ສຳຫຼວດທຸລະກຳ</h2>
              <p className="text-muted-foreground">
                ເບິ່ງ ແລະ ຄົ້ນຫາທຸລະກຳທັງໝົດໃນລະບົບກະເປົາເງິນ
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          {/* Filter bar */}
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <div className="relative">
                  <SearchIcon className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="ຄົ້ນຫາ ID, ອ້າງອີງ, ໝາຍເຫດ..."
                    defaultValue={searchValue}
                    onChange={(e) => debounced(e.target.value)}
                    className="h-8 pl-8 sm:max-w-2xs"
                  />
                </div>
                <SimpleSelect
                  value={typeValue}
                  onValueChange={(val) => handleFilterChange("type", val)}
                  placeholder="ປະເພດ"
                  className="h-8 w-full sm:w-40"
                  options={TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={statusValue}
                  onValueChange={(val) => handleFilterChange("status", val)}
                  placeholder="ສະຖານະ"
                  className="h-8 w-full sm:w-36"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={refTypeValue}
                  onValueChange={(val) =>
                    handleFilterChange("referenceType", val)
                  }
                  placeholder="ອ້າງອີງ"
                  className="h-8 w-full sm:w-40"
                  options={REFERENCE_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: {
                        ...search,
                        filters: undefined,
                        search: undefined,
                        offset: 0,
                      },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="h-4 w-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<WalletTransactionItem, unknown>
            noDataMessage="ບໍ່ພົບທຸລະກຳ"
            isLoading={list.isLoading}
            columns={columns}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
